/**
 * Messinaville's public sessions and booking, and the private and group request. Sessions come from tours/sessions
 * through the App Proxy. While the read is on its way the list shimmers; a read that fails says so (never "nothing
 * open"); with nothing open or every session full, the page offers "Be first for new dates."; once a session has places,
 * the booking panel takes the sign-up's place beside the list. Booking holds places for 15 minutes (tours/hold) and goes to
 * checkout. The request posts tours/request once the theme's "Tours live in Cabinet" setting is on; until then it's
 * Shopify's contact form.
 *
 *   tours/sessions {}                               → { sessions: [{ id, type, date, label, left, priceCents }] }
 *   tours/hold { sessionId, quantity, needs, key }  → { ok: true, checkoutUrl } | { ok: false, reason, left? }
 *   tours/request { kind, name, email, guests, dates, needs } → { ok: true }
 */
import { hub, safeInvoice } from "./ms-hub.js";

/** What the dates section shows: the sessions (only one tour's, on that tour's page) and which state they're in: "cant"
 * (the read failed), "none" (nothing open), "full" (every session full), or "open" (one has places, so booking shows). */
export function sessionsView(read, type) {
  const ok = !!(read && read.ok && Array.isArray(read.data?.sessions));
  const all = ok ? read.data.sessions : [];
  const rows = type ? all.filter((s) => s.type === type) : all;
  const open = rows.some((s) => (s.left || 0) > 0);
  return { rows, notify: !open, booking: open, state: !ok ? "cant" : open ? "open" : rows.length ? "full" : "none" };
}

/** What a ticketed tour's card says once the sessions read is back: sold out when none of its sessions has places, no dates
 * open when it has none at all, and nothing (null) when it has places or the read failed. */
export function tourCardState(read, type) {
  if (!read || !read.ok) return null;
  const v = sessionsView(read, type);
  if (v.booking) return null;
  return { status: v.rows.length ? "Sold out for now" : "No dates open", cta: "Get new dates" };
}

/** What a tour's own "See the dates" says once the read is back: Get new dates when nothing has places; as it is when a
 * session has places, or when the read failed (a failed read never says there's nothing). */
export function datesCta(view) {
  return view && (view.state === "none" || view.state === "full") ? "Get new dates" : null;
}

const CHECK = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5"/></svg>';
const BELL = '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M10.268 21a2 2 0 0 0 3.464 0"/><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"/></svg>';
const LOADER = '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>';
const places = (n) => `${n} place${n === 1 ? "" : "s"} left`;
/** The most places one hold takes online (the platform's MAX_WEB_GUESTS); bigger groups ask for a private tour. */
export const MAX_ONLINE = 10;
const day = (iso, o) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-AU", { ...o, timeZone: "UTC" });
const fullDay = (iso) => `${day(iso, { weekday: "long" })} ${day(iso, { day: "numeric" })} ${day(iso, { month: "long" })}`;
const dayName = (iso) => `${day(iso, { weekday: "short" })} ${day(iso, { day: "numeric" })} ${day(iso, { month: "short" })}`;
const shortDay = (s) => `${day(s.date, { weekday: "short" })} ${day(s.date, { day: "numeric" })} ${day(s.date, { month: "short" })}, ${s.label}`;
/** Swaps a button's icon for the bell (Get new dates), keeping its size. */
const ringBell = (el) => { const ic = el?.querySelector("svg"); if (ic) ic.outerHTML = BELL.replace(/(width|height)="22"/g, `$1="${ic.getAttribute("width") || 22}"`); };

/** One session in the list: its date (read out in full), its time, places left, and Pick, which names the date; the picked
 * one's pill is solid with a tick. A full session says Sold out and can't be picked. */
export function rowHtml(s, pickedId) {
  const full = fullDay(s.date);
  const tile = `${day(s.date, { weekday: "short" })} ${day(s.date, { month: "short" })}`;
  const on = s.id === pickedId && s.left > 0;
  const act = s.left > 0
    ? `<button class="pill${on ? " picked" : ""}" type="button" data-pick="${esc(s.id)}" aria-label="${on ? "Picked" : "Pick"} ${esc(full)}, ${esc(s.label)}, ${places(s.left)}">${on ? `${CHECK}Picked` : "Pick"}</button>`
    : `<span class="status">Sold out</span>`;
  return `<li class="sess${on ? " on" : ""}${s.left > 0 ? "" : " full"}"><div class="when"><time datetime="${esc(s.date)}"><span class="n" aria-hidden="true">${day(s.date, { day: "numeric" })}</span>`
    + `<span class="t-label" aria-hidden="true">${esc(tile)}</span><span class="sr">${esc(full)}</span></time></div>`
    + `<div><div class="t-card">${esc(s.label)}</div>${s.left > 0 ? `<div class="t-small t-soft">${places(s.left)}</div>` : ""}</div><div class="btns">${act}</div></li>`;
}

/** What a refused hold says, given how many were asked for, and how many places the session has now (null when that's not
 * known). When it lowers the number to fit, it says so. */
export function holdReply(r, asked) {
  if (r?.data?.reason === "full" && typeof r.data.left === "number") {
    const n = r.data.left;
    if (n <= 0) return { say: "That session just sold out. Pick another date, or get new dates.", left: 0 };
    return asked > n ? { say: `Only ${places(n).replace(" left", "")} left now, so we’ve dropped you to ${n}. Pay for ${n === 1 ? "it" : "those"}, or pick another date.`, left: n }
      : { say: `Only ${places(n).replace(" left", "")} left now. Try again, or pick another date.`, left: n };
  }
  // Past its booking cut-off (or cancelled): it can't come back, so it goes from the list like a sold-out one.
  if (r?.reason === "closed") return { say: "Bookings for that date have closed. Pick another date, or get new dates.", left: 0, closed: true };
  if (r?.reason === "busy") return { say: "You’ve already got places held. Finish that checkout, or they free up within 15 minutes.", left: null };
  if (r?.reason === "crowded") return { say: "Lots of people are booking right now. Try again in a few minutes.", left: null };
  // The hub said what was wrong with the ask (a 400): say it, rather than "try again", which would never work.
  if (typeof r?.message === "string" && r.message) { const m = r.message.replace(/'/g, "’"); return { say: /[.!?]$/.test(m) ? m : `${m}.`, left: null }; }
  return { say: "We couldn’t hold your places just now. Try again in a moment.", left: null };
}

/** The line over Pay when someone's back with places still held: how many, and until when (Sydney time). */
export function heldLine(quantity, heldUntil) {
  const t = new Date(heldUntil).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", timeZone: "Australia/Sydney" }).replace(/\s/g, "").toLowerCase();
  return `Your ${quantity} place${quantity === 1 ? " is" : "s are"} held until ${t}.`;
}

/** Why the open dates are offered, and how many there are: one, or more. */
export function othersLine(why, n) {
  return `${why} ${n === 1 ? "This one still has places:" : "These still have places:"}`;
}

/** The open sessions offered when the picked one sells out at Pay: one pill each, named in full. */
export function othersHtml(rows, asked = 0) {
  // Named by the words it shows (so voice control finds it), with the places left said after, and shown too when there
  // are fewer than were asked for, so nobody's surprised when the number drops.
  return rows.filter((s) => s.left > 0).map((s) => `<button class="pill" type="button" data-pick="${esc(s.id)}">${esc(shortDay(s))}<span class="${s.left < asked ? "pill-sub" : "sr"}">, ${places(s.left)}</span></button>`).join("");
}

/** The kind of request a tour is: the one whose words (longer than two letters) its name shares most, first on a tie,
 * as the tour's own page picks it in Liquid. */
export function pickKind(name, kinds) {
  const n = String(name || "").toLowerCase();
  let pick = kinds[0], best = 0;
  for (const k of kinds) {
    const hits = k.toLowerCase().split(" ").filter((w) => w.length > 2 && n.includes(w)).length;
    if (hits > best) { best = hits; pick = k; }
  }
  return pick;
}

/** This browser's key for its hold, kept for the visit: pressing Pay again replaces the hold rather than stacking another.
 * A fresh key whenever storage won't keep one (private windows). */
export function holdKey(store, scope = "") {
  const fresh = () => (globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, "");
  // One key per tour, so holding Behind the scenes never lets an Erin hold go.
  const slot = scope ? `ms-tour-hold:${scope}` : "ms-tour-hold";
  try {
    const had = store?.getItem(slot);
    if (had && /^[A-Za-z0-9_-]{1,100}$/.test(had)) return had;
    const k = fresh();
    store?.setItem(slot, k);
    return k;
  } catch {
    return fresh();
  }
}

const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const money = (c) => `$${(c / 100).toLocaleString("en-AU", { minimumFractionDigits: c % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;

if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  class MsSessions extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.qty = 2;
      this.want = 2;
      this.list = this.querySelector("[data-sessions]");
      this.notify = document.querySelector("[data-tours-notify]");
      this.book = document.querySelector("[data-tours-book]");
      this.more = this.querySelector("[data-more-dates]");
      this.addEventListener("click", (e) => {
        const p = e.target.closest("[data-pick]");
        if (p) this.pick(p.dataset.pick);
        if (e.target.closest("[data-retry]")) this.load(true);
        if (e.target.closest("[data-more-dates]")) { this.chosen = null; this.signingUp = true; this.hush(); this.paintRows(); this.lines("later"); this.more.hidden = true; this.show("notify", true); }
      });
      if (this.book) {
        this.book.addEventListener("click", (e) => {
          const step = e.target.closest("[data-qty]");
          // At a limit the circle says so (aria-disabled) and stays put, keeping focus where it is.
          if (step && step.getAttribute("aria-disabled") !== "true") this.choose(this.qty + Number(step.dataset.qty));
          // More at the limit says why, the same words typing past it does.
          else if (step && step.dataset.qty === "1") this.announce(this.clampLine());
          if (e.target.closest("[data-pay]")) this.pay();
          // The dates still open, offered in the panel when the picked one sold out at Pay.
          const p = e.target.closest("[data-pick]");
          if (p) this.pick(p.dataset.pick);
        });
        this.book.querySelector("[data-book-qty]")?.addEventListener("change", (e) => this.choose(Number(e.target.value) || 1, true));
        this.book.querySelector("textarea")?.addEventListener("input", () => this.remember());
        // Back from checkout: the page can come back from the browser's cache with Pay still holding.
        // Back from checkout out of the browser's cache: Pay works again, and the dates are read afresh (a hold may have run
        // out or been paid while they were away).
        addEventListener("pageshow", (e) => { if (e.persisted) { this.idle(); this.load(); } });
      }
      // On a phone, the support chat steps aside while the booking panel is on screen (ms-layout.css).
      // The sign-up's panel counts too: on a sold-out tour, that's the one on screen.
      if ("IntersectionObserver" in window) {
        const seen = new Set();
        const io = new IntersectionObserver((es) => {
          es.forEach((x) => (x.isIntersecting ? seen.add(x.target) : seen.delete(x.target)));
          document.documentElement.classList.toggle("tb-on", seen.size > 0);
        });
        [this.book, this.notify].forEach((p) => p && io.observe(p));
      }
      // Signed up: the thanks takes focus, so it's heard.
      document.querySelector(".tour-notify [data-done]")?.focus();
      this.load();
    }

    async load(retry = false) {
      const sec = this.closest("section") || this;
      const checking = sec.querySelector("[data-sessions-checking]");
      const busy = (on) => { if (this.list) this.list.setAttribute("aria-busy", String(on)); if (checking) checking.hidden = !on; };
      busy(true);
      if (retry) {
        // Trying again looks and sounds like the first read: the shimmer back, "Checking the dates" said. Focus goes to
        // the heading first, so it isn't lost with the Try again that hides.
        sec.querySelector("[data-dates-heading]")?.focus();
        this.lines(null);
        const cant = this.querySelector("[data-sessions-cant]");
        if (cant) cant.hidden = true;
        if (this.list) { this.list.innerHTML = '<li class="skel-row" aria-hidden="true"><span class="skel dot"></span><div class="lines"><span class="skel line" style="width: 40%"></span><span class="skel line" style="width: 25%"></span></div></li>'.repeat(2); this.list.hidden = false; }
      }
      // Read with this tour's hold key, so someone back from checkout (even on a fresh load) gets their own hold back.
      const r = await hub("tours/sessions", { key: this.key() });
      const v = sessionsView(r, this.dataset.type);
      this.view = v;
      busy(false);
      this.lines(v.state);
      const cant = this.querySelector("[data-sessions-cant]");
      if (cant) cant.hidden = v.state !== "cant";
      this.paintRows();
      // The tour's own hero button: Get new dates (with the bell) when nothing has places.
      const cta = datesCta(v);
      if (cta) document.querySelectorAll("[data-dates-cta]").forEach((a) => {
        const t = [...a.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim());
        if (t) t.textContent = cta;
        ringBell(a);
      });
      if (this.more) this.more.hidden = !v.booking || this.signingUp;
      const mine = r.ok && r.data?.mine && v.rows.find((s) => s.id === r.data.mine.sessionId && s.left > 0) ? r.data.mine : null;
      // A hold this page knew of that the read no longer finds has ended (run out, or paid): said once the panel's painted.
      const was = this.mine;
      const ended = !!was && !mine;
      this.mine = mine;
      // When their hold runs out, the line over Pay stops saying it's held, and the dates are read again.
      clearTimeout(this.heldTimer);
      if (mine) this.heldTimer = setTimeout(() => { this.paintBook(); this.load(); }, Math.max(0, Date.parse(mine.heldUntil) - Date.now()) + 1000);
      // Their date: the one picked on this page, else the one kept on its history entry (a reload, or Back from checkout).
      const saved = !this.chosen && !mine ? (() => { try { return history.state?.msTour || null; } catch { return null; } })() : null;
      if (saved && Number(saved.want) > 0) this.want = Math.round(Number(saved.want));
      // Their access and dietary needs come back too (a reload, or a phone waking a sleeping tab, empties the box).
      if (saved?.needs && !mine) { const nb = this.book?.querySelector("textarea"); if (nb && !nb.value) nb.value = saved.needs; }
      const theirs = this.chosen || (saved && saved.id ? saved : null);
      // Signing up by choice: a re-read only repaints the dates, and the sign-up they're typing into stays.
      if (this.signingUp && v.booking) { this.lines("later"); return; }
      // Their own hold first; then their date, if it's still open; then the next one with places.
      const kept = theirs && v.rows.find((s) => s.id === theirs.id && s.left > 0);
      // Their date has sold out or closed since: say so and offer the open ones, never quietly swap in another date.
      if (!mine && theirs && !kept && v.booking) {
        const row = v.rows.find((s) => s.id === theirs.id);
        const name = dayName(theirs.date);
        const what = row ? `${name} has sold out.` : `bookings for ${name} have closed.`;
        this.offer(ended && was.sessionId === theirs.id ? `Your hold has ended, and ${what}` : row ? what : `Bookings for ${name} have closed.`);
        return;
      }
      const first = mine ? v.rows.find((s) => s.id === mine.sessionId) : kept || v.rows.find((s) => s.left > 0);
      if (mine) {
        // Back with places held: their date, number and needs come back, and the line over Pay says until when.
        this.want = mine.quantity;
        const needs = this.book?.querySelector("textarea");
        if (needs && mine.needs) needs.value = mine.needs;
      }
      // The hold on the date they're on has ended (said only for that date): with fewer places now, one line says both.
      const endedHere = ended && first && was.sessionId === first.id;
      const fewer = !!(kept || mine) && first && first.left < this.want;
      // A kept or held date with fewer places than they asked for says the drop, as picking it would (in the one line when
      // the hold's ended too).
      if (first && !document.querySelector(".tour-notify [data-done]")) this.pick(first.id, { quiet: true, say: fewer && !endedHere });
      else this.show("notify");
      // Their own date, not the next one, is what's picked: the line doesn't say otherwise.
      if (mine || kept) this.lines("chosen");
      if (endedHere && this.chosen) this.tell(fewer
        ? `Your hold has ended, and ${dayName(first.date)} now has ${places(first.left).replace(" left", "")}, so we’ve dropped you to ${first.left}.`
        : "Your 15-minute hold has ended. If you haven’t paid yet, press Pay to hold them again.");
      // A retry that fails again puts focus back on Try again, so the next Tab doesn't skip past it.
      if (retry && v.state === "cant") this.querySelector("[data-retry]")?.focus();
    }

    /** The line under the heading (it sits above both columns, so it's the section's, not this element's). */
    lines(state) {
      (this.closest("section") || this).querySelectorAll("[data-sessions-lead]").forEach((el) => { el.hidden = el.dataset.sessionsLead !== { open: "some", none: "none", full: "full", cant: "cant", later: "later", chosen: "chosen" }[state]; });
    }

    paintRows() {
      if (!this.list) return;
      this.list.innerHTML = this.view.rows.map((s) => rowHtml(s, this.chosen?.id)).join("");
      this.list.hidden = !this.view.rows.length;
    }

    /** The right column: the sign-up or the booking panel, never both, never neither. */
    show(which, focus = false) {
      const wait = document.querySelector("[data-tours-wait]");
      if (wait) wait.hidden = true;
      if (this.notify) this.notify.hidden = which !== "notify";
      if (this.book) this.book.hidden = which !== "book";
      if (focus) (which === "notify" ? this.notify?.querySelector("input[type=email]") : this.book?.querySelector(".sess-pick"))?.focus();
    }

    key() {
      // Kept for the browser, not the tab: back from checkout in a new tab still finds the hold.
      let store = null;
      try { store = localStorage; } catch {}
      if (!store) try { store = sessionStorage; } catch {}
      return holdKey(store, this.dataset.type || "");
    }

    /** Their date can't be booked any more (sold out or closed, at Pay or found on a re-read): nothing stays picked behind
     * it. With other dates open, the panel offers them, one tap each, under a line that says why; with none, the sign-up comes
     * back with the news on top. Focus goes to that line (said once, by focus), so it isn't lost with Pay or the date. */
    offer(why) {
      this.chosen = null;
      this.paintRows();
      const open = this.view.rows.filter((x) => x.left > 0);
      if (open.length) {
        this.book.classList.toggle("no-pick", true);
        const others = this.book.querySelector("[data-book-others]");
        others.querySelector("[data-others-line]").textContent = othersLine(why, open.length);
        others.querySelector("[data-book-others-list]").innerHTML = othersHtml(this.view.rows, this.want);
        // Nothing's picked now, so the line above doesn't say it is.
        this.lines("later");
        others.hidden = false;
        this.show("book");
        others.querySelector("#tb-others")?.focus();
        return;
      }
      this.view.booking = false;
      this.view.state = this.view.rows.length ? "full" : "none";
      this.lines(this.view.state);
      if (this.more) this.more.hidden = true;
      this.show("notify");
      // The sign-up keeps its own line: it's where the newsletter's said.
      const ns = this.notify?.querySelector("[data-notify-say]");
      if (ns) { ns.querySelector("[data-say]").textContent = why; ns.hidden = false; ns.focus(); }
      document.querySelectorAll("[data-dates-cta]").forEach((a) => { const t = [...a.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim()); if (t) t.textContent = "Get new dates"; ringBell(a); });
    }

    /** Keeps their date and number on this page's history entry, so a reload or Back from checkout finds them again (it says
     * nothing about a hold, so it never claims one ran out). */
    remember() {
      const s = this.chosen;
      if (!s) return;
      const needs = this.book?.querySelector("textarea")?.value || "";
      try { history.replaceState({ ...(history.state || {}), msTour: { id: s.id, want: this.want, date: s.date, label: s.label, needs } }, ""); } catch {}
    }

    /** Says something in the booking panel's alert line (cleared by the next pick or Pay). */
    tell(t) {
      const say = this.book?.querySelector("[data-book-say]");
      if (say) { say.querySelector("[data-say]").textContent = t; say.hidden = false; }
    }

    pick(id, { quiet = false, say = false } = {}) {
      const s = this.view.rows.find((x) => x.id === id && x.left > 0);
      if (!s) return;
      this.chosen = s;
      // Back from Get new dates: the line and the link come back with the booking.
      if (this.view.state === "open") { this.lines(quiet ? "open" : "chosen"); if (this.more) this.more.hidden = false; }
      this.hush();
      this.book?.classList.toggle("no-pick", false);
      const others = this.book?.querySelector("[data-book-others]");
      if (others) others.hidden = true;
      // A new date starts from the number asked for, not one an earlier refusal lowered; if it has fewer places, it says so.
      this.setQty(this.want, !quiet || say);
      this.signingUp = false;
      this.remember();
      this.paintRows();
      this.show("book", !quiet);
      if (!quiet) {
        this.book?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "nearest" });
      }
    }

    /** The number the person asked for (circles or typed), kept so a later date starts from it. */
    choose(n, typed = false) {
      this.want = Math.max(1, Math.round(n) || 1);
      this.setQty(this.want, typed);
      this.remember();
    }

    /** The most this session takes in one go: its places left, and never more than one hold takes online. */
    most() { return Math.max(1, Math.min(this.chosen?.left || 1, MAX_ONLINE)); }

    announce(t) {
      const live = this.book?.querySelector("[data-book-live]");
      if (live) { live.textContent = ""; setTimeout(() => { live.textContent = t; }, 60); }
    }

    setQty(n, typed = false) {
      const max = this.most();
      const want = Math.round(n) || 1;
      this.qty = Math.max(1, Math.min(max, want));
      this.clamped = typed && want > max;
      this.paintBook();
      // Typed past the limit: the lowered number is said, not only shown.
      if (this.clamped) this.announce(this.clampLine());
    }

    /** Why the number stops where it does: the places left, or the most one hold takes online. */
    clampLine() {
      const left = this.chosen?.left || 1;
      return left > MAX_ONLINE ? `${places(left)}. Up to ${MAX_ONLINE} at a time online.` : `Only ${places(left).replace(" left", "")} left, so we’ve dropped you to ${left}.`;
    }

    /** Clears a refusal from an earlier hold. */
    hush() {
      const say = this.book?.querySelector("[data-book-say]");
      if (say) { say.hidden = true; say.querySelector("[data-say]").textContent = ""; }
    }

    paintBook() {
      const b = this.book;
      if (!b || !this.chosen) return;
      const s = this.chosen;
      b.querySelector("[data-book-when]").textContent = shortDay(s);
      b.querySelector("[data-book-type]").textContent = s.type;
      b.querySelector("[data-book-price]").textContent = `${money(s.priceCents)}pp`;
      const q = b.querySelector("[data-book-qty]");
      q.value = String(this.qty);
      q.max = String(this.most());
      b.querySelector("[data-qty='-1']").setAttribute("aria-disabled", String(this.qty <= 1));
      b.querySelector("[data-qty='1']").setAttribute("aria-disabled", String(this.qty >= this.most()));
      const left = b.querySelector("[data-book-left]");
      // At the limit (typed past it, or up to the most one hold takes), the line says why, with the way for bigger groups.
      // A date that fits everyone they asked for comes first; the private tour is for when none does, or more than 10.
      const fits = this.want <= MAX_ONLINE && this.view.rows.find((x) => x.id !== s.id && x.left >= this.want);
      const big = fits ? ` <button type="button" class="t-inline-btn" data-pick="${esc(fits.id)}">${esc(`${day(fits.date, { weekday: "short" })} ${day(fits.date, { day: "numeric" })} ${day(fits.date, { month: "short" })}`)} has room for all ${this.want}</button>`
        : ' <a href="#others">Bigger group? Ask about a private tour.</a>';
      if (this.clamped || (this.qty >= MAX_ONLINE && s.left > MAX_ONLINE)) left.innerHTML = `${esc(this.clampLine())}${big}`;
      // Fewer than they asked for (a refusal, or a hold that ended, lowered it): the date that fits everyone is offered.
      else if (this.qty < this.want && fits) left.innerHTML = `${esc(places(s.left))}.${big}`;
      else left.textContent = places(s.left);
      // Their own places, still held: until when, while it's the same date and number (Pay with those keeps the hold).
      const hl = b.querySelector("[data-hold-line]");
      if (hl) {
        if (this.holdWords == null) this.holdWords = hl.innerHTML;
        const m = this.mine;
        const still = m && m.sessionId === s.id && m.quantity === this.qty && Date.parse(m.heldUntil) > Date.now();
        hl.innerHTML = still ? `<b>${esc(heldLine(m.quantity, m.heldUntil))}</b> Pay to keep them.` : this.holdWords;
      }
      // The total is a polite live region: it's only rewritten when it changes, so it isn't said twice.
      const set = (sel, t) => { const el = b.querySelector(sel); if (el.textContent !== t) el.textContent = t; };
      set("[data-book-each]", `${this.qty} × ${money(s.priceCents)}`);
      set("[data-book-total]", money(s.priceCents * this.qty));
      if (!this.busy) b.querySelector("[data-pay-label]").textContent = `Pay ${money(s.priceCents * this.qty)}`;
    }

    /** Pay while it holds: says so, stays focusable (aria-disabled, not disabled), and can't be pressed twice. */
    holding(on) {
      const btn = this.book.querySelector("[data-pay]");
      this.busy = on;
      btn.setAttribute("aria-disabled", String(on));
      // Said once: when Pay has focus its own new name says it, so the live line stays quiet.
      const live = this.book.querySelector("[data-book-live]");
      if (live) live.textContent = on && document.activeElement !== btn ? "Holding your places" : "";
      btn.classList.toggle("busy", on);
      // The padlock gives way to a turning loader while it holds, and comes back after.
      const ic = btn.querySelector("svg");
      if (on && ic) { this.lock = ic; ic.insertAdjacentHTML("afterend", LOADER); ic.remove(); }
      if (!on && this.lock) { btn.querySelector("svg")?.replaceWith(this.lock); this.lock = null; }
      if (on) btn.querySelector("[data-pay-label]").textContent = "Holding your places";
      else this.paintBook();
    }

    idle() { if (this.busy) this.holding(false); }

    async pay() {
      if (this.busy || !this.chosen) return;
      const say = this.book.querySelector("[data-book-say]");
      this.hush();
      this.holding(true);
      const r = await hub("tours/hold", { sessionId: this.chosen.id, quantity: this.qty, needs: this.book.querySelector("textarea")?.value || "", key: this.key() }, { timeoutMs: 30000 });
      const to = r.ok && r.data.checkoutUrl ? safeInvoice(r.data.checkoutUrl) : null;
      if (to) { location.href = to; return; }
      this.holding(false);
      const reply = holdReply(r, this.qty);
      if (reply.left != null) {
        this.chosen.left = reply.left;
        if (reply.left === 0) {
          // Sold out at the hold: nothing stays picked behind it. With other dates open the panel asks for one; with none,
          // the sign-up comes back under "every session's full".
          // A closed date goes from the list; a sold-out one stays, saying Sold out.
          if (reply.closed) this.view.rows = this.view.rows.filter((x) => x.id !== this.chosen.id);
          this.mine = null;
          const gone = dayName(this.chosen.date);
          this.offer(reply.closed ? `Bookings for ${gone} have closed.` : `${gone} just sold out.`);
          return;
        } else {
          this.setQty(this.qty);
          this.paintRows();
        }
      }
      say.querySelector("[data-say]").textContent = reply.say;
      say.hidden = false;
    }
  }
  if (!customElements.get("ms-sessions")) customElements.define("ms-sessions", MsSessions);

  // Ticketed tour cards (the hub, Other tours): once the sessions read is back, a card with no places says so on it.
  const ticketed = [...document.querySelectorAll(".tour[data-tour-type]")];
  if (ticketed.length) {
    hub("tours/sessions", {}).then((read) => {
      for (const card of ticketed) {
        const st = tourCardState(read, card.dataset.tourType);
        if (!st) continue;
        const ph = card.querySelector(".tour-ph");
        // Seen on the photo; heard after the tour's name, not before it.
        if (ph && !ph.querySelector(".status")) ph.insertAdjacentHTML("beforeend", `<span class="status tour-status" aria-hidden="true">${esc(st.status)}</span>`);
        const h = card.querySelector("h3");
        if (h && !card.querySelector(".tour-said")) h.insertAdjacentHTML("afterend", `<p class="sr tour-said">${esc(st.status)}</p>`);
        const label = card.querySelector(".btn");
        if (label) { [...label.childNodes].filter((n) => n.nodeType === 3 && n.textContent.trim()).forEach((n) => { n.textContent = st.cta; }); ringBell(label); }
      }
    });
  }

  // "Ask about this" on a page that has the request (Private hire): tick the kind that tour is, then go to the request.
  document.addEventListener("click", (e) => {
    const a = e.target.closest("a[data-kind]");
    const ask = a && document.querySelector(a.getAttribute("href"));
    const radios = ask ? [...ask.querySelectorAll(".tour-kinds input[type=radio]")] : [];
    if (!radios.length) return;
    const k = pickKind(a.dataset.kind, radios.map((r) => r.value));
    radios.forEach((r) => { r.checked = r.value === k; });
  });

  // The request, once tours live in Cabinet: posted to the hub instead of the contact form.
  document.querySelectorAll("form[data-tours-request]").forEach((form) => {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const get = (n) => form.elements[n]?.value?.trim() || "";
      const say = form.querySelector("[data-request-say]");
      const send = form.querySelector("[type=submit]");
      if (send?.disabled) return;
      if (send) send.disabled = true;
      const r = await hub("tours/request", { kind: get("kind"), name: get("name"), email: get("email"), guests: get("guests"), dates: get("dates"), needs: get("needs") });
      say.textContent = r.ok ? "Sent. We’ll come back to you with a date." : "We couldn’t send that just now. Try again in a moment.";
      say.hidden = false;
      if (send) send.disabled = false;
      if (r.ok) form.reset();
    });
  });
}
